import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
	Alert,
	Button,
	Card,
	Flex,
	Input,
	Segmented,
	Select,
	Spin,
	Statistic,
	Table,
	Tabs,
	Tag,
	Typography,
	message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
	CheckCircleFilled,
	ReloadOutlined,
	SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import Header from '../../../components/Header/index.tsx';
import {
	RobotProgramAuditFinding,
	RobotProgramAuditSeverity,
} from '../../../interfaces/VersaBuilt.Interface';
import {
	useGetRobotProgramAuditQuery,
	useLazyGetVSCPartDataQuery,
} from '../../../redux/api/versabuilt';
import {
	OpenRobotProgram,
	RobotProgramAction,
	RobotProgramActionsButton,
	RobotProgramModals,
} from '../../Orders/RobotPrograms/RobotProgramActions';
import { extractApiError } from '../../../utils/apiError';
import {
	LENGTH_PARAMETER_UNITS,
	toInches,
} from '../../Orders/RobotPrograms/units';

type SeverityFilter = 'all' | RobotProgramAuditSeverity;
type AuditTab = 'findings' | 'rules';

const SEVERITY_TAGS: Record<
	RobotProgramAuditSeverity,
	{ color: string; label: string }
> = {
	error: { color: 'red', label: 'Error' },
	warning: { color: 'gold', label: 'Warning' },
};

const SEVERITY_ORDER: Record<RobotProgramAuditSeverity, number> = {
	error: 0,
	warning: 1,
};

const formatValue = (field: string, value: unknown) => {
	if (typeof value === 'number' && field in LENGTH_PARAMETER_UNITS) {
		const unit = LENGTH_PARAMETER_UNITS[field];
		return `${Number(value.toFixed(3))} ${unit} (${Number(
			toInches(value, unit).toFixed(3)
		)} in)`;
	}
	return JSON.stringify(value);
};

const countPrograms = (
	findings: RobotProgramAuditFinding[],
	severity: RobotProgramAuditSeverity
) =>
	new Set(
		findings.filter((f) => f.severity === severity).map((f) => f.program_id)
	).size;

const RobotProgramAudit = () => {
	const { data, isLoading, isFetching, error, refetch } =
		useGetRobotProgramAuditQuery(undefined, {
			refetchOnMountOrArgChange: true,
		});
	const [activeTab, setActiveTab] = useState<AuditTab>('findings');
	const [severity, setSeverity] = useState<SeverityFilter>('all');
	const [rule, setRule] = useState<string | undefined>();
	const [search, setSearch] = useState('');
	const [messageApi, messageContext] = message.useMessage();
	const [fetchProgram] = useLazyGetVSCPartDataQuery();
	const [loadingFindingId, setLoadingFindingId] = useState<string | null>(null);
	const [openProgram, setOpenProgram] = useState<OpenRobotProgram | null>(null);
	const [useInches, setUseInches] = useState(true);

	// Findings carry only what the table shows, so the full program is loaded
	// when an action is picked, fresh each time so edits aren't made on stale JSON.
	const handleAction = async (
		finding: RobotProgramAuditFinding,
		action: RobotProgramAction
	) => {
		setLoadingFindingId(finding.id);
		try {
			const program = await fetchProgram(finding.program_id, false).unwrap();
			setOpenProgram({
				action,
				program,
				orderLineItemId: finding.order_line_item?.id ?? program.order_line_item,
				siblings: [program],
			});
		} catch (e) {
			messageApi.error(extractApiError(e));
		} finally {
			setLoadingFindingId(null);
		}
	};

	const findings = data?.findings ?? [];
	const rules = data?.rules ?? [];
	const windowDays = data
		? Math.round(dayjs(data.checked_at).diff(data.created_since, 'day', true))
		: null;
	const programsWithIssues = new Set(findings.map((f) => f.program_id)).size;
	const errorCount = findings.filter((f) => f.severity === 'error').length;
	const warningCount = findings.length - errorCount;

	const query = search.trim().toLowerCase();
	const visibleFindings = findings.filter(
		(f) =>
			(severity === 'all' || f.severity === severity) &&
			(!rule || f.rule === rule) &&
			(!query ||
				[
					f.program_name,
					f.program_description,
					f.order_line_item?.name,
					f.order?.name,
				]
					.filter(Boolean)
					.some((text) => String(text).toLowerCase().includes(query)))
	);

	const columns: ColumnsType<RobotProgramAuditFinding> = [
		{
			title: 'Severity',
			width: 110,
			sorter: (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
			defaultSortOrder: 'ascend',
			render: (_, f) => (
				<Tag color={SEVERITY_TAGS[f.severity].color}>
					{SEVERITY_TAGS[f.severity].label}
				</Tag>
			),
		},
		{
			title: 'Program',
			render: (_, f) => (
				<Flex vertical>
					<span className="font-medium">{f.program_name ?? '—'}</span>
					<Typography.Text type="secondary" className="text-xs">
						{f.program_description ?? ''}
					</Typography.Text>
				</Flex>
			),
		},
		{
			title: 'Issue',
			render: (_, f) => f.message,
		},
		{
			title: 'Value',
			render: (_, f) => (
				<Flex vertical>
					<span className="whitespace-nowrap">
						{formatValue(f.field, f.value)}
					</span>
					<Typography.Text type="secondary" className="text-xs" code>
						{f.field}
					</Typography.Text>
				</Flex>
			),
		},
		{
			title: 'Line Item',
			render: (_, f) =>
				f.order_line_item ? (
					<Link to={`/orders/line-items/${f.order_line_item.id}`}>
						{f.order_line_item.name}
					</Link>
				) : (
					'—'
				),
		},
		{
			title: 'Order',
			render: (_, f) =>
				f.order ? (
					<Link to={`/orders/${f.order.id}`}>{f.order.name}</Link>
				) : (
					'—'
				),
		},
		{
			title: 'Program Last Saved',
			width: 170,
			sorter: (a, b) =>
				dayjs(a.program_modified_time).valueOf() -
				dayjs(b.program_modified_time).valueOf(),
			render: (_, f) =>
				dayjs(f.program_modified_time).format('MMM D YYYY, h:mm A'),
		},
		{
			title: '',
			width: 110,
			fixed: 'right',
			render: (_, f) => (
				<RobotProgramActionsButton
					loading={loadingFindingId === f.id}
					onAction={(action) => handleAction(f, action)}
				/>
			),
		},
	];

	return (
		<>
			<Header
				title="Robot Program Audit"
				breadcrumbItems={[
					{ title: <Link to="/">Home</Link> },
					{ title: 'Audit' },
					{ title: 'Robot Programs' },
				]}
			/>

			{/* A gap rather than margins on the cards: antd's own `.ant-card`
			    reset outranks Tailwind's `mb-*`, so the cards ended up touching. */}
			<Flex vertical gap="middle" style={{ paddingBottom: 40 }}>
				{error && (
					<Alert
						type="error"
						showIcon
						message="The audit couldn't be run"
						description={extractApiError(error)}
					/>
				)}

				<Card loading={isLoading}>
					<Flex justify="space-between" align="start" wrap="wrap" gap="large">
						<Flex wrap="wrap" gap={48}>
							<Statistic
								title={
									windowDays
										? `Programs Created in the Last ${windowDays} Days`
										: 'Programs Checked'
								}
								value={data?.programs_checked ?? 0}
							/>
							<Statistic
								title="Programs With Errors"
								value={countPrograms(findings, 'error')}
								valueStyle={errorCount ? { color: '#cf1322' } : undefined}
							/>
							<Statistic
								title="Programs With Warnings"
								value={countPrograms(findings, 'warning')}
								valueStyle={warningCount ? { color: '#d48806' } : undefined}
							/>
							<Statistic
								title="Passing Programs"
								value={(data?.programs_checked ?? 0) - programsWithIssues}
								valueStyle={{ color: '#389e0d' }}
							/>
						</Flex>
						<Flex vertical align="end" gap="small">
							<Button
								icon={<ReloadOutlined />}
								loading={isFetching}
								onClick={refetch}
							>
								Run Again
							</Button>
							{data && (
								<Typography.Text type="secondary" className="text-xs">
									Checked {dayjs(data.checked_at).format('MMM D YYYY, h:mm A')}
								</Typography.Text>
							)}
						</Flex>
					</Flex>
				</Card>

				<Tabs
					type="card"
					size="small"
					activeKey={activeTab}
					onChange={(key) => setActiveTab(key as AuditTab)}
					tabBarStyle={{ marginBottom: 0 }}
					items={[
						{
							key: 'findings',
							label: `Findings (${findings.length})`,
							children: (
								<div className="w-full bg-white border-b border-r border-l p-6">
									<Spin spinning={isLoading}>
										{data && findings.length === 0 ? (
											<Flex vertical align="center" gap={4} className="py-10">
												<CheckCircleFilled
													style={{ fontSize: 28, color: '#52c41a', marginBottom: 8 }}
												/>
												<Typography.Text strong>No issues found</Typography.Text>
												<Typography.Text type="secondary">
													All {data.programs_checked} robot programs created in the last{' '}
													{windowDays} days pass every rule.
												</Typography.Text>
											</Flex>
										) : (
											<Flex vertical gap="middle">
												<Flex wrap="wrap" gap="middle">
													<Segmented<SeverityFilter>
														value={severity}
														onChange={setSeverity}
														options={[
															{
																label: `All (${findings.length})`,
																value: 'all',
															},
															{
																label: `Errors (${errorCount})`,
																value: 'error',
															},
															{
																label: `Warnings (${warningCount})`,
																value: 'warning',
															},
														]}
													/>
													<Select
														allowClear
														placeholder="All rules"
														value={rule}
														onChange={setRule}
														options={rules.map((r) => ({
															label: r.title,
															value: r.id,
														}))}
														style={{ minWidth: 280 }}
													/>
													<Input
														allowClear
														prefix={<SearchOutlined />}
														placeholder="Search program, line item or order"
														value={search}
														onChange={(e) => setSearch(e.target.value)}
														style={{ maxWidth: 320 }}
													/>
												</Flex>
												<Table
													rowKey="id"
													size="small"
													dataSource={visibleFindings}
													columns={columns}
													loading={isFetching}
													pagination={{
														pageSize: 50,
														hideOnSinglePage: true,
													}}
													scroll={{ x: 'max-content' }}
												/>
											</Flex>
										)}
									</Spin>
								</div>
							),
						},
						{
							key: 'rules',
							label: `Rules (${rules.length})`,
							children: (
								<div className="w-full bg-white border-b border-r border-l p-6">
									<Spin spinning={isLoading}>
										<Flex vertical gap="middle">
											{rules.map((r) => {
												const ruleFindings = findings.filter(
													(f) => f.rule === r.id
												);
												const errors = ruleFindings.filter(
													(f) => f.severity === 'error'
												).length;
												const warnings = ruleFindings.length - errors;
												return (
													<Flex
														key={r.id}
														justify="space-between"
														align="start"
														gap="middle"
														wrap="wrap"
													>
														<Flex vertical className="min-w-0 flex-1">
															<span className="font-medium">{r.title}</span>
															<Typography.Text
																type="secondary"
																className="text-xs"
															>
																{r.description}
															</Typography.Text>
														</Flex>
														<Flex gap="small" align="center">
															{ruleFindings.length === 0 ? (
																<Tag color="green">Passing</Tag>
															) : (
																<>
																	{errors > 0 && (
																		<Tag color="red">{errors} errors</Tag>
																	)}
																	{warnings > 0 && (
																		<Tag color="gold">
																			{warnings} warnings
																		</Tag>
																	)}
																	<Button
																		size="small"
																		type="link"
																		onClick={() => {
																			setRule(r.id);
																			setActiveTab('findings');
																		}}
																	>
																		Show
																	</Button>
																</>
															)}
														</Flex>
													</Flex>
												);
											})}
										</Flex>
									</Spin>
								</div>
							),
						},
					]}
				/>
			</Flex>

			<RobotProgramModals
				open={openProgram}
				onClose={() => setOpenProgram(null)}
				useInches={useInches}
				onChangeUnits={setUseInches}
			/>
			{messageContext}
		</>
	);
};

export default RobotProgramAudit;
